<script setup lang="ts">
import { ref, computed, provide, nextTick } from 'vue';
import {
  DynamicFormContext,
  DynamicFormEmits,
  DynamicFormField,
  DynamicFormFieldRef,
  DynamicFormProps,
  DynamicFormUpdateParam,
  FormFieldTypes,
} from '@/types/form/dynamic-form';
import { ScItem } from '@/types/sitecore';
import { merge } from 'lodash-es';
import utils from '@/utils';
import qs from 'qs';
const props = defineProps<DynamicFormProps>();
const emit = defineEmits<DynamicFormEmits>();
const formItems = ref<Record<string, any>>({});
const firstInvalidField = ref<DynamicFormFieldRef | null>(null);
const fields = computed(() => props.form?.fields);
const items = computed(() => fields.value?.items ?? []);
const hasItems = computed(() => items.value.length > 0);
const rootStyle = computed(() => {
  return {
    '--item-back-color': fields.value?.itemBackColor?.value,
    '--item-color': fields.value?.itemForeColor?.value,
    '--item-border-color': fields.value?.itemBorderColor?.value,
    '--item-curtain-back-color': fields.value?.itemCurtainBackColor?.value,
    '--item-curtain-color': fields.value?.itemCurtainColor?.value,
    '--checkbox-back-color': fields.value?.checkboxBackColor?.value,
    '--checkbox-border-color': fields.value?.checkboxBorderColor?.value,
    '--checkbox-color': fields.value?.checkboxForeColor?.value,
    '--checkbox-active-back-color': fields.value?.checkboxActiveBackColor?.value,
    '--checkbox-active-color': fields.value?.checkboxActiveForeColor?.value,
  };
});
// Register form element
const regFormEl = (fieldRef: DynamicFormFieldRef) => {
  if (fieldRef && fieldRef.name) {
    formItems.value[fieldRef.name] = fieldRef;
  }
};

// Delete element registration
const unRegFormEl = (fieldRef: DynamicFormFieldRef) => {
  if (fieldRef && fieldRef.name && formItems.value[fieldRef.name]) {
    delete formItems.value[fieldRef.name];
  }
};

// Handle icon click
const onIconClick = (iconKey: string, value: string) => {
  emit('iconClick', iconKey, value);
};

// Handle dropdown change
const onChange = (fieldType: string, fieldName: string, value: string, originValue?: any) => {
  emit('change', fieldType, fieldName, value, originValue);
};

// Get field by name
const getField = (key: string) => {
  if (formItems.value[key]) return formItems.value[key];
  const normalizedKey = key.toLowerCase();
  const matchedKey = Object.keys(formItems.value).find(
    (itemKey) => itemKey.toLowerCase() === normalizedKey
  );
  return matchedKey ? formItems.value[matchedKey] : null;
};

// Update fields
const updateFields = (updates: DynamicFormUpdateParam) => {
  const keys = Object.keys(updates);
  for (const key of keys) {
    const updateInfo = updates[key];
    const item = getField(key);
    if (!item) continue;

    // A dropdown must receive its options before restoring a selected value.
    if (updateInfo.options !== undefined) {
      if (typeof item.updateOptions === 'function') {
        item.updateOptions(updateInfo.options);
      }
    }
    if (Object.hasOwn(updateInfo, 'value')) {
      if (typeof item.updateValue === 'function') {
        item.updateValue(updateInfo.value);
      }
    }
    if (updateInfo.disabled !== undefined) {
      if (typeof item.setDisabled === 'function') {
        item.setDisabled(updateInfo.disabled);
      }
    }
    if (updateInfo.required !== undefined) {
      if (typeof item.setRequired === 'function') {
        item.setRequired(updateInfo.required);
      }
    }
    if (Object.hasOwn(updateInfo, 'startData')) {
      if (typeof item.setStartData === 'function') {
        item.setStartData(updateInfo.startData);
      }
    }
    if (Object.hasOwn(updateInfo, 'endData')) {
      if (typeof item.setEndData === 'function') {
        item.setEndData(updateInfo.endData);
      }
    }
    if (updateInfo.visible !== undefined) {
      item.visible = updateInfo.visible;
      if (typeof item.setVisible === 'function') {
        item.setVisible(updateInfo.visible);
      }
    }
  }
};

// Reset all fields
const resetFields = () => {
  const keys = Object.keys(formItems.value);
  for (const key of keys) {
    const item = formItems.value[key];
    if (typeof item.updateValue === 'function') {
      item.updateValue(null);
    }
  }
};

// Set all fields disabled state
const setDisabled = (isDisabled: boolean) => {
  const keys = Object.keys(formItems.value);
  for (const key of keys) {
    const item = formItems.value[key];
    if (typeof item.setDisabled === 'function') {
      item.setDisabled(isDisabled);
    }
  }
};

// Validate all fields
const validate = async () => {
  let valid = true;
  firstInvalidField.value = null;
  const errors = [];
  const data: Record<string, any> = {};
  if (fields.value?.externalData?.value) {
    try {
      data.externalData = qs.parse(fields.value.externalData.value);
    } catch (error) {
      console.error('Error parsing externalData:', error);
    }
  }

  const keys = Object.keys(formItems.value);
  for (const key of keys) {
    const item = formItems.value[key];
    if (!item.visible) continue;

    const fieldResult = await item.validate();
    data[key] = fieldResult.value;
    valid = valid && fieldResult.valid;
    if (!fieldResult.valid && !firstInvalidField.value) {
      firstInvalidField.value = item;
    }
    if (fieldResult.message) {
      errors.push(fieldResult.message);
    }
  }

  return {
    valid,
    errors,
    data,
  };
};

const focusFirstInvalid = () => {
  const field = firstInvalidField.value;
  if (!field) return;

  const element = field.el;
  const target = element?.matches?.('input, textarea, select, button, [tabindex]')
    ? element
    : element?.querySelector?.('input, textarea, select, button, [tabindex]');
  const focusTarget = target || element;

  (element || target)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  if (focusTarget instanceof HTMLElement) {
    if (!target) focusTarget.setAttribute('tabindex', '-1');
    nextTick(() => focusTarget.focus({ preventScroll: true }));
  }
};

// Get all form values
const getValues = () => {
  const values = {};
  const keys = Object.keys(formItems.value);
  for (const key of keys) {
    const item = formItems.value[key];
    if (item.visible && typeof item.getValue === 'function') {
      merge(values, {
        [key]: item.getValue(),
      });
    }
  }
  return values;
};

const getComponentType = (item: ScItem<DynamicFormField>) => {
  let fieldType = item?.fields?.fieldType?.fields?.phrase?.value;
  if (!fieldType) return 'FormInput';
  fieldType = utils.string.toPascalCase(fieldType)!;
  if (!FormFieldTypes.includes(fieldType)) return 'FormInput';
  return fieldType;
};

// Provide form context to child components
const formContext: DynamicFormContext = {
  regFormEl,
  unRegFormEl,
};
provide('formContext', formContext);

// Expose methods for parent component
defineExpose({
  updateFields,
  resetFields,
  setDisabled,
  validate,
  focusFirstInvalid,
  getValues,
  getField,
});
</script>
<template>
  <div class="c-dynamic-form" :style="rootStyle" v-if="hasItems">
    <div class="c-dynamic-form__items">
      <template v-for="(item, index) in items" :key="`${item.id}-${index}`">
        <component
          :is="getComponentType(item)"
          :item="item"
          @iconClick="onIconClick"
          @change="onChange"
        />
      </template>
    </div>
    <div class="c-dynamic-form__external" v-if="$slots.default">
      <slot />
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

.c-dynamic-form {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__title {
    font-family: 'Figtree', sans-serif;
    font-size: 24px;
    font-weight: 600;
    color: $color-black;
    line-height: 1.4;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__external {
    margin-top: 8px;
  }
}
</style>
